import { announceLiveRegion } from '@theme/live-region';

// <s-load-more> turns the collection's "next page" link into "Cargar más productos":
// it fetches the next paginated URL (same filters and sort, since Shopify builds paginate.next.url
// from the current query), appends only products not already in the grid, and points the link at
// the following page. Without JavaScript the link is a normal ?page=N link, so SEO and no-JS
// navigation keep working. Numbered pagination stays visible until a load succeeds.
class LoadMore extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#controller.signal });
    // Progressive enhancement: the server always renders numbered pagination. Only once this
    // component is running, and there is a next page to load, does the button replace it.
    if (this.querySelector('a[data-load-more]')) {
      this.#setPaginationHidden(true);
      this.setAttribute('data-ready', '');
    }
  }

  #setPaginationHidden(hidden) {
    const pagination = this.querySelector('[data-load-more-pagination]');
    if (pagination) pagination.hidden = hidden;
    // The collection template's CSS keys off data-load-failed so the fallback only returns after an error.
    this.toggleAttribute('data-load-failed', !hidden);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  async #onClick(event) {
    const link = event.target.closest('a[data-load-more]');
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.hasAttribute('aria-busy')) return;

    const grid = document.querySelector(this.dataset.grid || '#product-grid');
    const label = link.querySelector('[data-load-more-label]') || link;
    const errorEl = this.querySelector('[data-load-more-error]');
    const idleText = label.textContent;
    const signal = this.#controller?.signal;
    if (!grid) return;

    this.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');
    label.textContent = this.dataset.loadingLabel || idleText;
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch(link.href, { signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector(this.dataset.grid || '#product-grid');
      if (!nextGrid) throw new Error('grid missing');

      const known = new Set([...grid.querySelectorAll(':scope > [data-product-id]')].map((li) => li.dataset.productId));
      const added = [];
      for (const item of nextGrid.querySelectorAll(':scope > [data-product-id]')) {
        if (known.has(item.dataset.productId)) continue;
        known.add(item.dataset.productId);
        // Appended cards are below the fold: never compete with the page's LCP image.
        for (const img of item.querySelectorAll('img')) {
          img.loading = 'lazy';
          img.removeAttribute('fetchpriority');
        }
        added.push(document.importNode(item, true));
      }
      grid.append(...added);

      const nextLink = doc.querySelector('s-load-more a[data-load-more]');
      const nextStatus = doc.querySelector('s-load-more [data-load-more-status]');
      const status = this.querySelector('[data-load-more-status]');
      if (status && nextStatus) status.textContent = nextStatus.textContent;
      if (nextLink) {
        link.href = nextLink.href;
      } else {
        this.querySelector('[data-load-more-action]')?.remove();
      }

      // Numbered pages would now be misleading (the list spans several pages), keep them hidden.
      this.#setPaginationHidden(true);

      const liveRegion = this.querySelector('[data-load-more-live]');
      const template = this.dataset.loadedMessage || '';
      if (liveRegion && added.length) announceLiveRegion(liveRegion, template.replace('[count]', String(added.length)));

      // Keyboard and screen reader users continue from the first new product.
      added[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (errorEl) errorEl.hidden = false;
      // A failed load brings the numbered pages back so shoppers can always keep browsing.
      this.#setPaginationHidden(false);
    } finally {
      if (link.isConnected) {
        label.textContent = idleText;
        link.removeAttribute('aria-disabled');
      }
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-load-more')) {
  customElements.define('s-load-more', LoadMore);
}
